<template>
    <Head title="Boutique Boti Cargo Shipping" />

    <FrontLayout>
        <div class="rounded-2xl bg-gradient-to-r from-primaryOrange-600 to-primaryOrange-400 p-8 md:p-10 mb-4 text-white">
            <h1 class="text-3xl md:text-4xl font-bold">Boti Cargo Shipping Boutique</h1>

            <p class="mt-2 text-white/90 max-w-2xl">
                Découvrez nos articles disponibles et commandez directement via WhatsApp.
            </p>
        </div>

        <!-- LOADING -->
        <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-10">
            <div v-for="n in 10" :key="n" class="animate-pulse bg-white rounded-2xl border p-4">
                <div class="bg-gray-200 aspect-square rounded-xl"></div>
                <div class="h-4 bg-gray-200 mt-4 rounded w-3/4"></div>
                <div class="h-3 bg-gray-200 mt-2 rounded w-1/2"></div>
                <div class="h-6 bg-gray-200 mt-4 rounded w-1/3"></div>
            </div>
        </div>

        <!-- SEARCH + FILTERS -->
        <div v-if="!loading" class="bg-white rounded-2xl border shadow-sm p-3 mb-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input v-model="search" type="text"
                    placeholder="Rechercher un article..." class="form-input w-full"
                >

                <input v-model="minPrice" type="number"
                    placeholder="Prix minimum" class="form-input w-full"
                >

                <input v-model="maxPrice" type="number"
                    placeholder="Prix maximum" class="form-input w-full"
                >
            </div>
        </div>

        <!-- CATEGORIES -->
        <div v-if="!loading" class="mb-4">
            <div class="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                <button @click="selectedCategory = null"
                    class="px-3 py-1 text-xs rounded-full border transition"
                    :class="selectedCategory === null ? 'bg-primaryOrange text-white' : 'bg-white hover:bg-gray-100'"
                >Toutes
                </button>

                <button v-for="category in categories" :key="category.id"
                    @click="selectedCategory = category.id"
                    class="px-3 py-1 text-xs rounded-full border transition whitespace-nowrap"
                    :class="selectedCategory === category.id ? 'bg-primaryOrange text-white' : 'bg-white hover:bg-gray-100'"
                >{{ category.name }}
                </button>
            </div>
        </div>

        <!-- EMPTY STATE (COMING SOON GLOBAL) -->
        <div v-if="!loading && items.data.length === 0"
            class="bg-white rounded-2xl border p-16 text-center mb-10"
        >
            <div class="text-6xl mb-3">🚧</div>

            <h2 class="text-2xl font-bold text-gray-700">Coming Soon</h2>

            <p class="text-gray-500 mt-2 max-w-md mx-auto">
                Notre boutique est en préparation. Revenez très bientôt pour découvrir nos produits.
            </p>
        </div>

        <!-- PRODUCTS -->
        <div v-else-if="filteredItems.length"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-10"
        >
            <div v-for="item in filteredItems" :key="item.id"
                class="group bg-white rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition border overflow-hidden"
            >
                <div class="relative aspect-square bg-gray-100 overflow-hidden">
                    <img v-if="item.media?.length"
                        :src="item.media[0].original_url"
                        class="w-full h-full object-cover group-hover:scale-105 transition"
                    >
                    <div v-else class="flex items-center justify-center h-full text-gray-400">
                        <i class="fas fa-image text-6xl"></i>
                    </div>
                </div>

                <div class="p-4">
                    <h2 class="font-bold text-gray-700 line-clamp-2 text-sm">{{ item.short_name }}</h2>

                    <p class="text-xs text-gray-500 mt-1">{{ item.short_description }}</p>

                    <!-- <div class="mt-2 text-xl font-bold text-primaryOrange">
                        {{ formatAmount(item.price, currency) }}
                    </div> -->

                    <div class="mt-4 flex gap-2">
                        <Link :href="route('front.store.show', item)"
                            class="flex-1 text-center py-1 text-sm rounded-lg bg-gray-100 hover:bg-gray-200"
                        >Détails
                        </Link>

                        <a :href="whatsappLink(item)" target="_blank"
                            class="flex-1 text-center py-1 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700"
                        >WhatsApp</a>
                    </div>
                </div>
            </div>
        </div>

        <!-- EMPTY FILTER STATE -->
        <div v-else-if="!loading" class="bg-white rounded-2xl border p-12 text-center mb-10">
            <div class="text-6xl mb-3">🔎</div>

            <h3 class="text-xl font-semibold text-gray-600">Aucun résultat</h3>

            <p class="text-gray-400 mt-2">Essayez de modifier vos filtres.</p>

            <button @click="resetFilters" class="mt-4 px-4 py-2 bg-primaryOrange text-white rounded-lg">
                Réinitialiser
            </button>
        </div>
    </FrontLayout>
</template>

<script>
    import FrontLayout from '@/Layouts/FrontLayout.vue'
    import { Head, Link } from '@inertiajs/inertia-vue3'
    import CurrencyMixins from '@/Mixins/CurrencyMixins'

    export default {
        components: {
            Head,
            Link,
            FrontLayout
        },

        mixins: [CurrencyMixins],

        props: {
            items: Object,
            currency: Object,
            categories: Array
        },

        data() {
            return {
                search: '',
                selectedCategory: null,
                minPrice: null,
                maxPrice: null,
                loading: true
            }
        },

        mounted() {
            setTimeout(() => {
                this.loading = false
            }, 400)
        },

        computed: {
            filteredItems() {
                return this.items.data.filter(item => {

                    const term = this.search.toLowerCase()

                    const matchSearch =
                        !term ||
                        item.name?.toLowerCase().includes(term) ||
                        item.reference?.toLowerCase().includes(term)

                    const matchCategory =
                        !this.selectedCategory ||
                        item.categories.some(
                            category => category.id === this.selectedCategory
                        )

                    const matchMin =
                        !this.minPrice ||
                        item.price >= Number(this.minPrice)

                    const matchMax =
                        !this.maxPrice ||
                        item.price <= Number(this.maxPrice)

                    return (
                        matchSearch &&
                        matchCategory &&
                        matchMin &&
                        matchMax
                    )
                })
            }
        },

        methods: {
            resetFilters() {
                this.search = ''
                this.selectedCategory = null
                this.minPrice = null
                this.maxPrice = null
            },

            whatsappLink(item) {
                return route('track.whatsapp', item)
            }
        }
    }
</script>

<style scoped>
    .no-scrollbar::-webkit-scrollbar {
        display: none;
    }

    .no-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
</style>
